/**
 * IndexedDB 存储实现（兜底方案）
 *
 * FS API 不可用时使用。数据结构相同，仅存储介质不同。
 * 用 idb 库简化 IndexedDB 操作。
 *
 * 对齐 architecture.md §五「兜底：IndexedDB」
 */

import type { openDB } from 'idb';
import type { Block } from '@hump/shared-types';
import type { Storage } from './index';

const DB_NAME = 'hump';
const DB_VERSION = 2;

// 延迟加载 idb（MVP 安装后生效，当前为类型占位）
let dbPromise: ReturnType<typeof openDB> | null = null;

async function getDB() {
  if (!dbPromise) {
    const { openDB: open } = await import('idb');
    dbPromise = open(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // v1: create base stores
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains('docIndex'))
            db.createObjectStore('docIndex', { keyPath: 'id' });
          if (!db.objectStoreNames.contains('blocks')) {
            const blockStore = db.createObjectStore('blocks', { keyPath: ['docId', 'id'] });
            blockStore.createIndex('docId', 'docId');
          }
          if (!db.objectStoreNames.contains('vocab'))
            db.createObjectStore('vocab');
          if (!db.objectStoreNames.contains('library'))
            db.createObjectStore('library');
        }
        // v2: ensure docId index exists on blocks store
        if (oldVersion < 2) {
          if (db.objectStoreNames.contains('blocks')) {
            const blockStore = transaction.objectStore('blocks');
            if (!blockStore.indexNames.contains('docId')) {
              blockStore.createIndex('docId', 'docId');
            }
          }
        }
      },
    });
  }
  return dbPromise;
}

export const IndexedDbStorage: Storage = {
  async readDocIndex(id) {
    const db = await getDB();
    return db.get('docIndex', id);
  },

  async writeDocIndex(index) {
    const db = await getDB();
    await db.put('docIndex', index);
  },

  async readBlock(docId, blockId) {
    const db = await getDB();
    return db.get('blocks', [docId, blockId]);
  },

  async writeBlock(docId, block) {
    const db = await getDB();
    await db.put('blocks', block);
  },

  async listBlocks(docId) {
    const db = await getDB();
    const all = (await db.getAllFromIndex(
      'blocks',
      'docId',
      docId
    )) as Block[];
    return all.map((b) => b.id);
  },

  async readVocab() {
    const db = await getDB();
    return db.get('vocab', 'main');
  },

  async writeVocab(vocab) {
    const db = await getDB();
    await db.put('vocab', vocab, 'main');
  },

  async readLibrary() {
    const db = await getDB();
    return db.get('library', 'main');
  },

  async writeLibrary(lib) {
    const db = await getDB();
    await db.put('library', lib, 'main');
  },

  async deleteDoc(docId) {
    const db = await getDB();
    await db.delete('docIndex', docId);
    const tx = db.transaction('blocks', 'readwrite');
    const index = tx.store.index('docId');
    let cursor = await index.openCursor(docId);
    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  },
};
